import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Toolbar,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  PlayArrow as PlayIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  ConnectWithoutContact as ConnectIcon
} from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useDraggable,
  useDroppable
} from '@dnd-kit/core';
import {
  WorkflowDefinition,
  WorkflowStep,
  WorkflowConnection,
  WorkflowStepType,
  WorkflowCategory,
  WORKFLOW_CATEGORIES,
  CreateWorkflowRequest,
  UpdateWorkflowRequest
} from '../../types/workflows';

interface WorkflowBuilderProps {
  workflowId?: string;
  initialWorkflow?: WorkflowDefinition;
  onSave?: (workflow: CreateWorkflowRequest | UpdateWorkflowRequest) => Promise<void>;
  onCancel?: () => void;
  onTest?: (workflow: WorkflowDefinition) => void;
}

interface StepPaletteItem {
  type: WorkflowStepType;
  label: string;
  description: string;
  icon: string;
  category: 'control' | 'action' | 'data' | 'integration';
}

const STEP_PALETTE: StepPaletteItem[] = [
  { type: 'start', label: 'Start', description: 'Workflow entry point', icon: '▶️', category: 'control' },
  { type: 'end', label: 'End', description: 'Workflow completion', icon: '⏹️', category: 'control' },
  { type: 'task', label: 'Task', description: 'Manual task assignment', icon: '📋', category: 'action' },
  { type: 'decision', label: 'Decision', description: 'Conditional branching', icon: '❓', category: 'control' },
  { type: 'form', label: 'Form', description: 'Data collection form', icon: '📝', category: 'data' },
  { type: 'rule', label: 'Rule', description: 'Business rule execution', icon: '⚡', category: 'action' },
  { type: 'api-call', label: 'API Call', description: 'External service integration', icon: '🌐', category: 'integration' },
  { type: 'email', label: 'Email', description: 'Send email notification', icon: '📧', category: 'action' },
  { type: 'delay', label: 'Delay', description: 'Wait for specified time', icon: '⏰', category: 'control' },
  { type: 'script', label: 'Script', description: 'Custom script execution', icon: '💻', category: 'action' },
  { type: 'parallel', label: 'Parallel', description: 'Parallel execution paths', icon: '↕️', category: 'control' },
  { type: 'loop', label: 'Loop', description: 'Iterative execution', icon: '🔄', category: 'control' },
];

// Draggable Palette Item Component
const DraggablePaletteItem: React.FC<{ item: StepPaletteItem }> = ({ item }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `palette-${item.type}`,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      sx={{
        cursor: 'grab',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 0.5,
        '&:hover': {
          bgcolor: 'action.hover'
        },
        '&:active': {
          cursor: 'grabbing'
        }
      }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>
        <Typography>{item.icon}</Typography>
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        secondary={item.description}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  );
};

// Draggable Workflow Step Component
const DraggableWorkflowStep: React.FC<{
  step: WorkflowStep;
  selectedStep: WorkflowStep | null;
  mode: 'select' | 'connect' | 'pan';
  onStepClick: (step: WorkflowStep) => void;
  onDeleteStep: (stepId: string) => void;
}> = ({ step, selectedStep, mode, onStepClick, onDeleteStep }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: step.id,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  const paletteItem = STEP_PALETTE.find(item => item.type === step.type);
  
  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => onStepClick(step)}
      sx={{
        position: 'absolute',
        left: step.position.x,
        top: step.position.y,
        width: 120,
        height: 80,
        cursor: mode === 'connect' ? 'crosshair' : 'grab',
        border: selectedStep?.id === step.id ? 2 : 1,
        borderColor: selectedStep?.id === step.id ? 'primary.main' : 'divider',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1,
        '&:hover': {
          bgcolor: 'action.hover'
        },
        '&:active': {
          cursor: 'grabbing'
        }
      }}
    >
      <Typography variant="h5">{paletteItem?.icon}</Typography>
      <Typography variant="caption" align="center" sx={{ mt: 0.5 }}>
        {step.name}
      </Typography>
      
      {selectedStep?.id === step.id && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteStep(step.id);
          }}
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            bgcolor: 'error.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'error.dark'
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Paper>
  );
};

// Droppable Canvas Component
const DroppableCanvas: React.FC<{
  children: React.ReactNode;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  zoom: number;
  pan: { x: number; y: number };
}> = ({ children, canvasRef, zoom, pan }) => {
  const { setNodeRef } = useDroppable({
    id: 'canvas',
  });

  const setRefs = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node);
    if (canvasRef.current !== node) {
      (canvasRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  }, [setNodeRef, canvasRef]);

  return (
    <Box
      ref={setRefs}
      id="canvas"
      sx={{
        flexGrow: 1,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'grey.50',
        transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
        transformOrigin: 'top left'
      }}
    >
      {children}
    </Box>
  );
};

const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({
  workflowId,
  initialWorkflow,
  onSave,
  onTest
}) => {
  const [workflow, setWorkflow] = useState<Partial<WorkflowDefinition>>({
    name: initialWorkflow?.name || 'New Workflow',
    description: initialWorkflow?.description || '',
    category: initialWorkflow?.category || 'Other',
    steps: initialWorkflow?.steps || [],
    connections: initialWorkflow?.connections || [],
    variables: initialWorkflow?.variables || [],
    triggers: initialWorkflow?.triggers || [],
    ...initialWorkflow
  });

  const [selectedStep, setSelectedStep] = useState<WorkflowStep | null>(null);
  const [drawerOpen] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [mode, setMode] = useState<'select' | 'connect' | 'pan'>('select');
  const [connectionStart, setConnectionStart] = useState<{ stepId: string; outputId?: string } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setIsDragging(false);

    if (!over) return;

    // Check if dragging from palette to canvas
    if (active.id.toString().startsWith('palette-')) {
      const stepType = active.id.toString().replace('palette-', '') as WorkflowStepType;
      const rect = canvasRef.current?.getBoundingClientRect();
      
      if (rect && event.activatorEvent && event.delta) {
        // Calculate the actual drop position by combining the initial position with the delta
        const pointerEvent = event.activatorEvent as PointerEvent;
        const dropX = pointerEvent.clientX + event.delta.x;
        const dropY = pointerEvent.clientY + event.delta.y;
        
        const newStep: WorkflowStep = {
          id: `step-${Date.now()}`,
          name: `${stepType.charAt(0).toUpperCase() + stepType.slice(1)} Step`,
          type: stepType,
          description: '',
          position: {
            x: (dropX - rect.left) / zoom - pan.x,
            y: (dropY - rect.top) / zoom - pan.y,
          },
          configuration: {}
        };

        setWorkflow(prev => ({
          ...prev,
          steps: [...(prev.steps || []), newStep]
        }));
      }
    }
    // Handle moving existing steps
    else if (over.id === 'canvas') {
      const stepId = active.id.toString();
      const step = workflow.steps?.find(s => s.id === stepId);
      
      if (step && event.delta) {
        const updatedSteps = workflow.steps?.map(s => 
          s.id === stepId 
            ? { 
                ...s, 
                position: {
                  x: s.position.x + event.delta.x / zoom,
                  y: s.position.y + event.delta.y / zoom
                }
              }
            : s
        );

        setWorkflow(prev => ({
          ...prev,
          steps: updatedSteps
        }));
      }
    }
  }, [workflow.steps, zoom, pan]);

  const handleStepClick = useCallback((step: WorkflowStep) => {
    if (mode === 'connect') {
      if (!connectionStart) {
        setConnectionStart({ stepId: step.id });
      } else if (connectionStart.stepId !== step.id) {
        // Create connection
        const newConnection: WorkflowConnection = {
          id: `connection-${Date.now()}`,
          sourceStepId: connectionStart.stepId,
          sourceOutput: 'default',
          targetStepId: step.id,
          targetInput: 'default'
        };

        setWorkflow(prev => ({
          ...prev,
          connections: [...(prev.connections || []), newConnection]
        }));

        setConnectionStart(null);
        setMode('select');
      }
    } else {
      setSelectedStep(step);
    }
  }, [mode, connectionStart]);

  const handleDeleteStep = useCallback((stepId: string) => {
    setWorkflow(prev => ({
      ...prev,
      steps: prev.steps?.filter(s => s.id !== stepId) || [],
      connections: prev.connections?.filter(c => 
        c.sourceStepId !== stepId && c.targetStepId !== stepId
      ) || []
    }));
    setSelectedStep(null);
  }, []);

  const handleDeleteConnection = useCallback((connectionId: string) => {
    setWorkflow(prev => ({
      ...prev,
      connections: prev.connections?.filter(c => c.id !== connectionId) || []
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave) return;

    const request: CreateWorkflowRequest | UpdateWorkflowRequest = {
      name: workflow.name || 'Untitled Workflow',
      description: workflow.description,
      category: workflow.category || 'Other',
      steps: workflow.steps || [],
      connections: workflow.connections || [],
      variables: workflow.variables || [],
      triggers: workflow.triggers || []
    };

    if (workflowId) {
      (request as UpdateWorkflowRequest).version = initialWorkflow?.version || '1.0.0';
    }

    await onSave(request);
  }, [workflow, workflowId, initialWorkflow, onSave]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / 1.2, 0.1));
  }, []);

  const handleCenter = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const renderStepPalette = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Step Palette
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Drag steps onto the canvas to build your workflow
        </Typography>
        
        {['control', 'action', 'data', 'integration'].map(category => (
          <Box key={category} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
              {category}
            </Typography>
            <List dense>
              {STEP_PALETTE.filter(item => item.category === category).map(item => (
                <DraggablePaletteItem key={item.type} item={item} />
              ))}
            </List>
          </Box>
        ))}
      </CardContent>
    </Card>
  );

  const renderWorkflowStep = (step: WorkflowStep) => (
    <DraggableWorkflowStep
      key={step.id}
      step={step}
      selectedStep={selectedStep}
      mode={mode}
      onStepClick={handleStepClick}
      onDeleteStep={handleDeleteStep}
    />
  );

  const renderConnections = () => {
    return workflow.connections?.map(connection => {
      const sourceStep = workflow.steps?.find(s => s.id === connection.sourceStepId);
      const targetStep = workflow.steps?.find(s => s.id === connection.targetStepId);
      
      if (!sourceStep || !targetStep) return null;

      const x1 = sourceStep.position.x + 60; // Center of step
      const y1 = sourceStep.position.y + 40;
      const x2 = targetStep.position.x + 60;
      const y2 = targetStep.position.y + 40;

      return (
        <line
          key={connection.id}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#666"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          style={{ cursor: 'pointer' }}
          onClick={() => handleDeleteConnection(connection.id)}
        />
      );
    });
  };

  const renderStepProperties = () => {
    if (!selectedStep) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step Properties
          </Typography>
          
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={selectedStep.name}
              onChange={(e) => {
                const updatedSteps = workflow.steps?.map(s =>
                  s.id === selectedStep.id ? { ...s, name: e.target.value } : s
                );
                setWorkflow(prev => ({ ...prev, steps: updatedSteps }));
                setSelectedStep({ ...selectedStep, name: e.target.value });
              }}
              fullWidth
              size="small"
            />
            
            <TextField
              label="Description"
              value={selectedStep.description || ''}
              onChange={(e) => {
                const updatedSteps = workflow.steps?.map(s =>
                  s.id === selectedStep.id ? { ...s, description: e.target.value } : s
                );
                setWorkflow(prev => ({ ...prev, steps: updatedSteps }));
                setSelectedStep({ ...selectedStep, description: e.target.value });
              }}
              fullWidth
              multiline
              rows={2}
              size="small"
            />

            <Typography variant="subtitle2" color="primary">
              Step Type: {selectedStep.type}
            </Typography>

            <Divider />

            <Typography variant="subtitle2">
              Configuration
            </Typography>
            
            {/* Step-specific configuration would go here */}
            <Typography variant="body2" color="text.secondary">
              Step-specific configuration options will be implemented based on step type.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Sidebar */}
        <Drawer
          variant="persistent"
          anchor="left"
          open={drawerOpen}
          sx={{
            width: 300,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 300,
              boxSizing: 'border-box',
              position: 'relative'
            },
          }}
        >
          <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            {renderStepPalette()}
            {renderStepProperties()}
          </Box>
        </Drawer>

        {/* Main Canvas Area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Toolbar */}
          <Toolbar sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              value={workflow.name}
              onChange={(e) => setWorkflow(prev => ({ ...prev, name: e.target.value }))}
              variant="outlined"
              size="small"
              sx={{ mr: 2, minWidth: 200 }}
            />

            <FormControl size="small" sx={{ mr: 2, minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={workflow.category || 'Other'}
                onChange={(e) => setWorkflow(prev => ({ ...prev, category: e.target.value as WorkflowCategory }))}
                label="Category"
              >
                {WORKFLOW_CATEGORIES.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" spacing={1}>
              <Tooltip title="Toggle Connection Mode">
                <IconButton
                  onClick={() => setMode(mode === 'connect' ? 'select' : 'connect')}
                  color={mode === 'connect' ? 'primary' : 'default'}
                >
                  <ConnectIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom In">
                <IconButton onClick={handleZoomIn}>
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom Out">
                <IconButton onClick={handleZoomOut}>
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Center View">
                <IconButton onClick={handleCenter}>
                  <CenterIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Workflow Settings">
                <IconButton onClick={() => setSettingsOpen(true)}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>

              <Button
                variant="outlined"
                startIcon={<PlayIcon />}
                onClick={() => onTest?.(workflow as WorkflowDefinition)}
                disabled={!workflow.steps?.length}
              >
                Test
              </Button>

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!workflow.name?.trim()}
              >
                Save
              </Button>
            </Stack>
          </Toolbar>

          {/* Canvas */}
          <DroppableCanvas canvasRef={canvasRef} zoom={zoom} pan={pan}>
            {/* Grid background */}
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            >
              <defs>
                <pattern
                  id="grid"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 20 0 L 0 0 0 20"
                    fill="none"
                    stroke="#e0e0e0"
                    strokeWidth="1"
                  />
                </pattern>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="10"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#666"
                  />
                </marker>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
              {renderConnections()}
            </svg>

            {/* Workflow Steps */}
            {workflow.steps?.map(renderWorkflowStep)}

            {/* Connection preview */}
            {mode === 'connect' && connectionStart && (
              <Typography
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  bgcolor: 'primary.main',
                  color: 'white',
                  p: 1,
                  borderRadius: 1
                }}
              >
                Click another step to create connection
              </Typography>
            )}
          </DroppableCanvas>
        </Box>

        <DragOverlay>
          {isDragging ? (
            <Paper sx={{ p: 1, opacity: 0.8 }}>
              <Typography>Dragging...</Typography>
            </Paper>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Workflow Settings</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Description"
              value={workflow.description || ''}
              onChange={(e) => setWorkflow(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
            
            <Typography variant="subtitle2">
              Variables ({workflow.variables?.length || 0})
            </Typography>
            
            <Typography variant="subtitle2">
              Triggers ({workflow.triggers?.length || 0})
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowBuilder;